import { isEmpty, isNil, partial } from 'lodash';

import { MACHINE_STATE, PersonalisedQuoteMachine, personalisedQuoteMachine } from './PersonalisedQuote.machine';
import { PersonalisedQuotePayloadPartial } from './templates/Forms/PersonalizedQuoteForm.type';

export type StateOrderMappings = { [key: string]: number };

const isBeforeCurrentOrder = (
  currentStepState: MACHINE_STATE | undefined,
  stateOrderMappings: StateOrderMappings,
  targetStep: MACHINE_STATE,
): boolean =>
  stateOrderMappings && currentStepState
    ? stateOrderMappings[currentStepState] > stateOrderMappings[targetStep]
    : false;

export const getPersonalisedQuoteProgress = (
  payload: PersonalisedQuotePayloadPartial,
  isLoggedIn: boolean,
  isExistingAccount: boolean,
  currentStepState: MACHINE_STATE | undefined,
  stateOrderMappings: StateOrderMappings,
): { progress: number; maxStep: MACHINE_STATE } => {
  const checkAndPushProgress = (
    payloadCondition: boolean,
    currentStep: MACHINE_STATE,
    stateCondition: boolean,
    targetStepIfConditionFalse: MACHINE_STATE,
    targetStepIfConditionTrue?: MACHINE_STATE,
  ) => {
    progressArray.push(payloadCondition && isBeforeCurrentOrderPartial(currentStep));
    if (payloadCondition) {
      maxStep = stateCondition && targetStepIfConditionTrue ? targetStepIfConditionTrue : targetStepIfConditionFalse;
    }
  };

  const initialProgress = 0.1;
  const progressArray: boolean[] = [];
  let maxStep: MACHINE_STATE = MACHINE_STATE.START;

  // For reducing code verbosity
  const isBeforeCurrentOrderPartial = partial(isBeforeCurrentOrder, currentStepState, stateOrderMappings);

  // Check for loan info
  const hasLoanInfo = !isEmpty(payload.loanDetails?.preferredBuildYear);
  checkAndPushProgress(hasLoanInfo, MACHINE_STATE.START, false, MACHINE_STATE.LOAN_TYPE);

  // Check for buy type
  const hasBuyType = !isNil(payload.loanType);
  checkAndPushProgress(
    hasBuyType,
    MACHINE_STATE.LOAN_TYPE,
    isLoggedIn,
    MACHINE_STATE.EMAIL,
    MACHINE_STATE.PERSONAL_DETAILS,
  );

  // Check for email and determine if existing account
  const hasEmail = !isNil(payload.email);
  checkAndPushProgress(hasEmail, MACHINE_STATE.EMAIL, isExistingAccount, MACHINE_STATE.PHONE, MACHINE_STATE.SIGN_IN);

  // Check for phone
  const hasPhone = !isNil(payload.phone);
  checkAndPushProgress(hasPhone, MACHINE_STATE.PHONE, false, MACHINE_STATE.PERSONAL_DETAILS);

  // Check for buyer info
  const hasBuyerInfo = !isNil(payload.personalDetails);
  checkAndPushProgress(hasBuyerInfo, MACHINE_STATE.PERSONAL_DETAILS, false, MACHINE_STATE.FAMILY_AND_RESIDENTIAL);

  // Check for family info
  const hasFamilyInfo = !isNil(payload.familyDetails);
  checkAndPushProgress(
    hasFamilyInfo,
    MACHINE_STATE.FAMILY_AND_RESIDENTIAL,
    false,
    MACHINE_STATE.EMPLOYMENT_AND_CITIZENSHIP,
  );

  // Check for employment info
  const hasEmploymentInfo = !isNil(payload.employmentAndCitizenshipDetails);
  checkAndPushProgress(
    hasEmploymentInfo,
    MACHINE_STATE.EMPLOYMENT_AND_CITIZENSHIP,
    isLoggedIn,
    MACHINE_STATE.CREATE_ACCOUNT,
    MACHINE_STATE.TERMS_AND_CONDITIONS,
  );

  // Check for sign up info
  if (!isLoggedIn) {
    const hasAccountSignupDetails = !isNil(payload.accountSignupDetails);
    checkAndPushProgress(hasAccountSignupDetails, MACHINE_STATE.CREATE_ACCOUNT, false, MACHINE_STATE.VERIFY_ACCOUNT);
  }

  // Check for verification info
  if (!isLoggedIn) {
    const hasVerificationDetails = !isNil(payload.accountSignupVerification);
    checkAndPushProgress(
      hasVerificationDetails,
      MACHINE_STATE.VERIFY_ACCOUNT,
      false,
      MACHINE_STATE.TERMS_AND_CONDITIONS,
    );
  }

  // Check for T&C info
  const hasTermsAndConditionsInfo = !isNil(payload.termsAndConditionsDetails);
  checkAndPushProgress(
    hasTermsAndConditionsInfo,
    MACHINE_STATE.TERMS_AND_CONDITIONS,
    false,
    MACHINE_STATE.FINANCE_QUOTES_LIST,
  );

  // Check for quote info
  const hasQuoteInfo = !isNil(payload.financeQuote);
  checkAndPushProgress(hasQuoteInfo, MACHINE_STATE.FINANCE_QUOTES_LIST, false, MACHINE_STATE.END);

  // Calculate progress by counting the true in progressArray
  const progress = progressArray.filter((value) => value).length / progressArray.length;

  return { progress: initialProgress + (1 - initialProgress) * progress, maxStep };
};

/**
 * Lookup object used to map state keys to associated state information used in helper functions.
 */
const stateKeysToStateData = personalisedQuoteMachine.stateIds
  .filter((id) => id !== personalisedQuoteMachine.id)
  .map((id) => personalisedQuoteMachine.getStateNodeById(id))
  .reduce(
    (acc, { id, key, definition }) => ({
      ...acc,
      [key]: {
        definition,
        value: id.replace(new RegExp(`^${personalisedQuoteMachine.id}?.`), ''),
      },
    }),
    {} as Record<
      MACHINE_STATE,
      { definition: PersonalisedQuoteMachine['definition']; value: NonNullable<PersonalisedQuoteMachine['target']> }
    >,
  );

/**
 * Get the associated state key.
 */
export const getStateKey = (stateKey: MACHINE_STATE) => stateKeysToStateData[stateKey].definition.key;
